export const AUDIENCE_STORAGE_KEY = "croc-ops.perspective.audience.v1";

export const presenterAudiences = [
  "telecom-admin",
  "it-staff",
  "executive",
  "student",
] as const;

export type PresenterAudience = (typeof presenterAudiences)[number];

export type AudienceDefinition = {
  id: PresenterAudience;
  label: string;
  description: string;
  emphasis: string;
};

export const audienceDefinitions: Record<PresenterAudience, AudienceDefinition> = {
  "telecom-admin": {
    id: "telecom-admin",
    label: "TeleCom Admin",
    description: "Service ownership, voice and network impact, and safe action.",
    emphasis: "Explains what must stay connected, who owns it, and how to verify recovery.",
  },
  "it-staff": {
    id: "it-staff",
    label: "IT Staff",
    description: "Devices, queues, dependencies, evidence, and execution.",
    emphasis: "Explains the records and working evidence needed to complete the task.",
  },
  executive: {
    id: "executive",
    label: "Executive",
    description: "Continuity, population impact, risk, and accountability.",
    emphasis: "Explains the operational consequence without converting estimates into facts.",
  },
  student: {
    id: "student",
    label: "Student",
    description: "Campus availability and experience in privacy-safe language.",
    emphasis: "Explains what a learner may notice and what the college is doing about it.",
  },
};

export function isPresenterAudience(value: unknown): value is PresenterAudience {
  return typeof value === "string" && presenterAudiences.includes(value as PresenterAudience);
}

export function audienceFromSearch(search: string): PresenterAudience | null | "invalid" {
  const params = new URLSearchParams(search);
  if (!params.has("audience")) return null;
  const value = params.get("audience");
  return isPresenterAudience(value) ? value : "invalid";
}

export function resolveInitialAudience(search: string, storedValue: unknown): PresenterAudience {
  const urlAudience = audienceFromSearch(search);
  if (urlAudience === "invalid") return "telecom-admin";
  if (urlAudience) return urlAudience;
  return isPresenterAudience(storedValue) ? storedValue : "telecom-admin";
}

export function searchWithAudience(search: string, audience: PresenterAudience): string {
  const params = new URLSearchParams(search);
  params.set("audience", audience);
  const next = params.toString();
  return next ? `?${next}` : "";
}

export function searchWithoutInvalidAudience(search: string): string {
  const params = new URLSearchParams(search);
  if (audienceFromSearch(search) === "invalid") params.delete("audience");
  const next = params.toString();
  return next ? `?${next}` : "";
}

